/**
 * PropertyPicker — choose which Property a project's intake is scoped to.
 * (M5-S4 multi-property.)
 *
 * Reads the project's authoritative `projects/{pid}.propertyRefs[]`, loads each
 * Property doc for its address, then:
 *   • 0 properties → onSelect('') once (no UI; the intake carries no property scope)
 *   • 1 property   → onSelect(it) once (auto; no UI — nothing to choose)
 *   • >1           → radio-style card list; onSelect(chosen) on pick
 *
 * Tokens only — var(--accent)/var(--accent-wash) for the selected state.
 */
import { useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getProperty } from '../lib/propertyStore';
import type { PropertyRef } from '../lib/projectStore';
import { pickerDecision, type PropertyOption } from '../lib/propertyPicker';

export default function PropertyPicker({
  pid,
  value,
  onSelect,
}: {
  pid: string;
  /** Currently-selected property id (for the >1 highlight). */
  value?: string;
  onSelect: (propertyId: string) => void;
}) {
  const [options, setOptions] = useState<PropertyOption[] | null>(null);
  // Fire the 0/1 auto-resolution exactly once per pid.
  const decided = useRef(false);

  useEffect(() => {
    let live = true;
    decided.current = false;
    setOptions(null);
    (async () => {
      let refs: PropertyRef[] = [];
      try {
        const snap = await getDoc(doc(db, 'projects', pid));
        const d = (snap.exists() ? snap.data() : {}) as { propertyRefs?: PropertyRef[] };
        refs = Array.isArray(d.propertyRefs) ? d.propertyRefs : [];
      } catch {
        refs = [];
      }
      // Load each linked property for its address (best-effort; skip missing).
      const loaded: PropertyOption[] = [];
      for (const r of refs) {
        try {
          const p = await getProperty(r.propertyId);
          loaded.push({
            propertyId: r.propertyId,
            label: (p?.address && p.address.trim()) || r.propertyId,
            role: r.role,
          });
        } catch {
          /* skip this ref */
        }
      }
      if (!live) return;
      setOptions(loaded);
      const dec = pickerDecision(loaded);
      if (!decided.current && (dec.kind === 'none' || dec.kind === 'auto')) {
        decided.current = true;
        onSelect(dec.kind === 'auto' ? dec.propertyId : '');
      }
    })();
    return () => {
      live = false;
    };
    // onSelect is intentionally excluded — it is a fresh closure each render and
    // the once-guard (decided) handles the auto/none fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  if (options == null) {
    return <p style={hint}>Loading properties…</p>;
  }

  // 0 or 1 property → no UI (auto-resolved above).
  if (pickerDecision(options).kind !== 'choose') return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={lbl}>Which property is this intake for?</label>
      {options.map((o) => {
        const on = value === o.propertyId;
        return (
          <button
            key={o.propertyId}
            type="button"
            onClick={() => onSelect(o.propertyId)}
            aria-pressed={on}
            style={on ? cardOn : card}
          >
            <span style={radio(on)} />
            <span style={{ display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{o.label}</span>
              <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                {o.propertyId}
                {o.role ? ` · ${o.role}` : ''}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ───────────────────────── styles (tokens only) ───────────────────────── */

const hint: React.CSSProperties = { fontSize: 12, color: 'var(--ink-mute)' };
const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ink-soft)',
  marginBottom: 4,
};
const card: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  textAlign: 'left',
  border: '1px solid var(--rule-strong)',
  background: '#fff',
  borderRadius: 'var(--r-sm)',
  padding: '10px 12px',
  cursor: 'pointer',
};
const cardOn: React.CSSProperties = {
  ...card,
  border: '1px solid var(--accent)',
  background: 'var(--accent-wash)',
};
function radio(on: boolean): React.CSSProperties {
  return {
    width: 14,
    height: 14,
    borderRadius: '50%',
    flexShrink: 0,
    border: `2px solid ${on ? 'var(--accent)' : 'var(--rule-strong)'}`,
    background: on ? 'var(--accent)' : 'transparent',
    boxShadow: on ? 'inset 0 0 0 2px #fff' : 'none',
  };
}
